// Pause looping CSS effects outside the viewport without a per-frame JS loop.
export function installAnimationVisibility() {
  if (typeof IntersectionObserver === 'undefined') return () => {};
  const tracked = new Set();
  const observer = new IntersectionObserver(entries => {
    entries.forEach(({ target, isIntersecting }) => {
      target.toggleAttribute('data-animation-offscreen', !isIntersecting);
    });
  }, { rootMargin: '80px' });
  const track = (target, pseudo = null) => {
    if (!(target instanceof Element) || tracked.has(target)) return;
    const style = getComputedStyle(target, pseudo);
    if (!style.animationIterationCount.split(',').some(v => v.trim() === 'infinite')) return;
    tracked.add(target);
    observer.observe(target);
  };
  const onStart = event => track(event.target, event.pseudoElement || null);
  const onVisibility = () => {
    document.documentElement.toggleAttribute('data-animation-hidden', document.hidden);
  };
  document.addEventListener('animationstart', onStart, true);
  document.addEventListener('visibilitychange', onVisibility);
  onVisibility();
  // Existing animations also matter when loaded through hot reload.
  document.getAnimations?.().forEach(animation => {
    const effect = animation.effect;
    if (effect) track(effect.target, effect.pseudoElement || null);
  });
  const mutations = new MutationObserver(records => {
    if (!records.some(record => record.removedNodes.length)) return;
    tracked.forEach(target => {
      if (!target.isConnected) {
        observer.unobserve(target);
        target.removeAttribute('data-animation-offscreen');
        tracked.delete(target);
      }
    });
  });
  mutations.observe(document.body, { childList: true, subtree: true });
  return () => {
    observer.disconnect();
    mutations.disconnect();
    tracked.forEach(target => target.removeAttribute('data-animation-offscreen'));
    document.documentElement.removeAttribute('data-animation-hidden');
    document.removeEventListener('animationstart', onStart, true);
    document.removeEventListener('visibilitychange', onVisibility);
  };
}
